import React, { useEffect, useState } from 'react';
import { createReferral } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatInternationalPhoneNumber, validatePhoneNumber } from '../utils/phoneUtils';

const ReferCandidateCard = ({ onReferralSuccess }) => {
    const { isAuthenticated, showSignIn } = useAuth();
    const { showSuccess } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    
    // Auto-clear inline message after 5 seconds
    useEffect(() => {
        if (!message) return;
        const timer = setTimeout(() => setMessage(null), 5000);
        return () => clearTimeout(timer);
    }, [message]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check authentication first
        if (!isAuthenticated) {
            showSignIn();
            return;
        }
        
        const formData = new FormData(e.target);
        const candidateName = (formData.get('candidateName') || '').trim();
        const mobileNumber = formData.get('mobileNumber');
        
        console.log('[ReferCandidateCard] Form data:');
        console.log('[ReferCandidateCard] candidateName:', candidateName);
        console.log('[ReferCandidateCard] mobileNumber:', mobileNumber);
        
        // Basic validation
        if (!candidateName || !mobileNumber) {
            console.log('[ReferCandidateCard] Validation failed: Missing fields');
            console.log('[ReferCandidateCard] candidateName empty:', !candidateName);
            console.log('[ReferCandidateCard] mobileNumber empty:', !mobileNumber);
            setMessage({ type: 'error', text: 'Please fill in all fields' });
            return;
        }
        
        // Name validation: letters and spaces only, 2-50 chars
        const nameValid = /^[A-Za-z ]{2,50}$/.test(candidateName);
        if (!nameValid) {
            console.log('[ReferCandidateCard] Validation failed: Invalid candidate name');
            setMessage({ type: 'error', text: 'Candidate Name should contain letters and spaces only (2-50 characters)' });
            return;
        }
        
        // Format and validate mobile number (supports international numbers)
        const cleanMobile = formatInternationalPhoneNumber(mobileNumber);
        console.log('[ReferCandidateCard] Original mobile:', mobileNumber);
        console.log('[ReferCandidateCard] Formatted mobile:', cleanMobile);
        
        const validation = validatePhoneNumber(cleanMobile);
        if (!validation.isValid) {
            console.log('[ReferCandidateCard] Validation failed:', validation.errorMessage);
            setMessage({ type: 'error', text: validation.errorMessage });
            return;
        }
        
        console.log('[ReferCandidateCard] All validations passed, proceeding with API call');
        
        setIsSubmitting(true);
        setMessage(null);
        
        try {
            // Create referral using the API (direct referral without job ID)
            const result = await createReferral({
                jobId: null, // Direct referral
                candidates: [{
                    name: candidateName,
                    mobile: cleanMobile
                }]
            });
            
            console.log('[ReferCandidateCard] Referral result:', result);
            console.log('[ReferCandidateCard] Result success:', result.success);
            console.log('[ReferCandidateCard] Result message:', result.message);
            console.log('[ReferCandidateCard] Result data:', result.data);
            
            if (result.success) {
                const apiMsg = (result && typeof result.message === 'string') ? result.message.trim() : '';

                // Only show green success when API message matches exactly
                if (apiMsg === 'Referrals added successfully') {
                    showSuccess && showSuccess(apiMsg);
                    e.target.reset();
                    setMessage(null);
                    if (onReferralSuccess) {
                        onReferralSuccess();
                    }
                } else {
                    const msg = apiMsg || 'No new candidates were added';
                    // Suppress toast for inline error display
                    setMessage({ type: 'error', text: msg });
                }
            } else {
                // Handle different response formats
                let errorMessage = 'Failed to add candidate. Please try again.';
                
                if (result.message && result.message !== 'Unknown response') {
                    errorMessage = result.message;
                } else if (result.data && result.data.message) {
                    errorMessage = result.data.message;
                }
                
                // Suppress toast; show inline error only
                setMessage({ type: 'error', text: errorMessage });
            }
            
        } catch (error) {
            console.error('[ReferCandidateCard] Error creating referral:', error);
            console.error('[ReferCandidateCard] Error message:', error.message);
            console.error('[ReferCandidateCard] Error stack:', error.stack);
            console.error('[ReferCandidateCard] Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            
            setMessage({ 
                type: 'error', 
                text: `An error occurred while sending the referral: ${error.message}. Please try again.` 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="sidebar-card">
            <h3 style={{
                margin: '0 0 1rem 0',
                color: '#1f2937',
                fontSize: '1.125rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
            }}>
                <i className="fas fa-gift" style={{ color: '#2563eb' }}></i>
                Refer a Candidate
            </h3>
            
            <h4 style={{ 
                marginBottom: '0.5rem', 
                color: '#2563eb', 
                fontSize: '1rem', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
            }}>
                Earn <i className="fas fa-indian-rupee-sign" style={{ fontSize: '0.875rem' }}></i> 500 per Referral
            </h4>
            
            <p style={{ 
                marginBottom: '0.75rem', 
                color: '#8b5cf6', 
                fontSize: '0.8125rem', 
                fontStyle: 'italic', 
                fontWeight: '500' 
            }}>
                Rewarding connections that work
            </p>
            
            <p style={{ 
                marginBottom: '1rem', 
                color: '#6b7280', 
                fontSize: '0.875rem', 
                lineHeight: '1.5',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                flexWrap: 'wrap'
            }}>
                Refer someone to Channel Play, and if they join and complete one month with us, you will earn <i className="fas fa-indian-rupee-sign" style={{ fontSize: '0.75rem', color: '#6b7280' }}></i> 500 as a referral bonus.
            </p>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: '0' }}>
                    <label className="form-label" style={{
                        display: 'block',
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '0.875rem',
                        marginBottom: '0.25rem'
                    }}>Candidate Name</label>
                    <input 
                        type="text" 
                        name="candidateName"
                        className="form-input" 
                        placeholder="Enter full name" 
                        required 
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            boxSizing: 'border-box',
                            transition: 'all 0.2s ease'
                        }}
                        maxLength="50"
                        pattern="[A-Za-z ]{2,50}"
                        title="Use letters and spaces only (2-50 characters)"
                        autoComplete="off"
                        onInput={(e) => {
                            // Allow only letters and spaces; collapse multiple spaces
                            const cleaned = e.target.value
                                .replace(/[^A-Za-z ]/g, '')
                                .replace(/\s{2,}/g, ' ');
                            if (e.target.value !== cleaned) {
                                e.target.value = cleaned;
                            }
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = '#2563eb';
                            e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                        }}
                    />
                </div>
                
                <div className="form-group" style={{ marginBottom: '0' }}>
                    <label className="form-label" style={{
                        display: 'block',
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '0.875rem',
                        marginBottom: '0.25rem'
                    }}>Mobile Number</label>
                    <input 
                        type="tel" 
                        name="mobileNumber"
                        className="form-input" 
                        placeholder="Enter candidate phone number" 
                        required 
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            boxSizing: 'border-box',
                            transition: 'all 0.2s ease'
                        }}
                        maxLength="15"
                        title="Enter candidate phone number"
                        inputMode="numeric"
                        autoComplete="tel"
                        onInput={(e) => {
                            // Format international phone numbers automatically
                            const formatted = formatInternationalPhoneNumber(e.target.value);
                            e.target.value = formatted;
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = '#2563eb';
                            e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                        }}
                    />
                </div>
                
                {/* Message Display */}
                {message && (
                    <div style={{
                        padding: '0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        background: message.type === 'success' ? '#dcfce7' : '#fef2f2',
                        color: message.type === 'success' ? '#16a34a' : '#dc2626',
                        border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                        {message.text}
                    </div>
                )}
                
                <button 
                    type="submit" 
                    disabled={isSubmitting}
                    style={{
                        width: '100%',
                        background: isSubmitting ? '#9ca3af' : '#2563eb',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1.25rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                        if (!isSubmitting) {
                            e.target.style.background = '#1d4ed8';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isSubmitting) {
                            e.target.style.background = '#2563eb';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }
                    }}
                >
                    <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                    {isSubmitting ? 'Sending...' : 'Send Referral'}
                </button>
            </form>
        </div>
    );
};

export default ReferCandidateCard;
